<template>
  <div id="app" class="notranslate" translate="no">
    <Nav />
    <router-view/>
    <SummaryModal ref="summaryModal" v-show="isSummaryModalVisible" :summary="summary" @close="closeSummaryModal" />
    <ScorecardModal ref="scorecardModal" v-show="isModalVisible" :scorecard="scorecard" @close="closeModal" />
    <InfoModal ref="infoModal" v-show="isInfoModalVisible" @close="closeInfoModal" />
    <Footer />
    <loading-screen :is-loading="isLoading"></loading-screen>
  </div>
</template>

<script>
import Nav from './components/partials/Nav.vue';
import Footer from './components/partials/Footer.vue';
import ScorecardModal from "./components/ScorecardModal.vue";
import SummaryModal from "./components/SummaryModal.vue";
import InfoModal from "./components/InfoModal.vue";
import Loader from "./components/Loader";
import { GameType } from './enum/gameType.js';
import { ScorecardCalculations } from './utils/scorecardCalculations.js';
import { scorecardService } from './services/scorecardService.js';
import SocketioService from './services/socketio.service.js';

import { getBearerToken } from './utils/jwtHelper.js';

export default {
  name: 'app',
  data() {
    return {
      GameType,
      debugMode: (process.env.VUE_APP_DEBUG_MODE === "true"),
      tickerItems: [],
      tickerItem: {},
      isModalVisible: false,
      isInfoModalVisible: false,
      isSummaryModalVisible: false,
      scorecard: null,
      summary: null,
      isLoading: true,
      adminUsers: null
    }
  },
  components: {
    Nav,
    Footer,
    ScorecardModal,
    SummaryModal,
    InfoModal,
    'loading-screen': Loader
  },
  created() {
    console.log("SocketioService.setupSocketConnection()");
    SocketioService.setupSocketConnection();
  },
  beforeUnmount() {
    console.log("SocketioService.disconnect()");
    SocketioService.disconnect();
  },  
  methods: {
    isSuper() {
      return (this.$auth.user.email === 'jamie.symonds@gmail.com');
    },
    
    isAdmin() {
      if (!this.$auth.isAuthenticated) {
        return false;
      }
      return this.adminUsers?.some(admin => 
        admin.email === this.$auth.user.email
      ) || false;
    },
    
    getAdminUsers() {
      const administrators = [
        { "email": "logroller1@yahoo.com" },
        { "email": "jamie.symonds@gmail.com" },
        { "email": "timberloungetv@gmail.com" },
        { "email": "wildaxetv@gmail.com" },
        { "email": "suzyatwood24@gmail.com" },
        { "email": "axes2coach@gmail.com" }
      ];
      this.adminUsers = administrators;
    },
    
    showSummaryModal(summary) {
      this.summary = summary;
      this.isSummaryModalVisible = true;
    },
    
    closeSummaryModal() {
      this.isSummaryModalVisible = false;
      this.summary = {};
    },
    
    showModal(scorecard) {
      this.isModalVisible = true;
      this.scorecard = scorecard;
    },
    
    closeModal() {
      this.isModalVisible = false;
      this.scorecard = {};
    },
    
    showInfoModal() {
      this.isInfoModalVisible = true;
    },
    
    
    closeInfoModal() {
      this.isInfoModalVisible = false;
    },
    
    // API Helper methods
    getApiUrl(endpoint) {
      const baseUrl = this.debugMode 
        ? "http://localhost:8080" 
        : "https://axes2api.azurewebsites.net";
      return `${baseUrl}/${endpoint}`;
    },
    
    getApiToken() {
      return getBearerToken();
    },
    
    // Calculation methods - delegating to utility service
    calculateTotalThrowsForRound(player, round, totalRoundsNum) {
      return ScorecardCalculations.calculateTotalThrowsForRound(player, round, totalRoundsNum);
    },
    
    calculateTotalThrows(player, scorecard) {
      return ScorecardCalculations.calculateTotalThrows(player, scorecard);
    },
    
    calculateTotalBullseyesFinals(player, scorecard, lastRoundOnly = false) {
      return ScorecardCalculations.calculateTotalBullseyesFinals(player, scorecard, lastRoundOnly);
    },
    
    calculateTotalBullseyes(player, scorecard) {
      return ScorecardCalculations.calculateTotalBullseyes(player, scorecard);
    },
    
    calculateTotalWinsFinals(player, scorecard, lastRoundOnly = false) {
      return ScorecardCalculations.calculateTotalWinsFinals(player, scorecard, lastRoundOnly);
    },
    
    calculateTotalWins(player, scorecard) {
      return ScorecardCalculations.calculateTotalWins(player, scorecard);
    },
    
    calculateTotalLoss(player, scorecard) {
      return ScorecardCalculations.calculateTotalLoss(player, scorecard);
    },
    
    calculateTotalGP(player, scorecard) {
      return ScorecardCalculations.calculateTotalGP(player, scorecard);
    },
    
    calculateBullseyesForRound(player, round, totalNumRounds) {
      return ScorecardCalculations.calculateBullseyesForRound(player, round, totalNumRounds);
    },
    
    calculateThrowPoints(player, gameData) {
      return ScorecardCalculations.calculateThrowPoints(player, gameData);
    },
    
    calculateThrows(player, scorecard) {
      return ScorecardCalculations.calculateThrows(player, scorecard);
    },
    
    calculateThrowsForRound(player, round, totalNumRounds) {
      return ScorecardCalculations.calculateThrowsForRound(player, round, totalNumRounds);
    },
    
    countThis(bye, round, totalRoundsNum) {
      return ScorecardCalculations.countThis(bye, round, totalRoundsNum);
    },
    
    calculateThrowsFinals(player, scorecard) {
      return ScorecardCalculations.calculateThrowsFinals(player, scorecard);
    },
    
    calculateTotalThrowsFinals(player, scorecard, lastRoundOnly = false) {
      return ScorecardCalculations.calculateTotalThrowsFinals(player, scorecard, lastRoundOnly);
    },
    
    calculateAxeHitOnRound(playerId, round, basicScore) {
      return ScorecardCalculations.calculateAxeHitOnRound(playerId, round, basicScore);
    },
    
    calculateAxeHit(player, scorecard, basicScore) {
      return ScorecardCalculations.calculateAxeHit(player, scorecard, basicScore);
    },
    
    calculateAverageThrow(player, scorecard) {
      return ScorecardCalculations.calculateAverageThrow(player, scorecard);
    },
    
    calculateAverageThrowForRound(mPlayer, round, gameType, totalRoundsNum = null) {
      return ScorecardCalculations.calculateAverageThrowForRound(mPlayer, round, gameType, totalRoundsNum);
    },
    
    calculateAverageThrowFinals(player, scorecard, lastRoundOnly = false) {
      return ScorecardCalculations.calculateAverageThrowFinals(player, scorecard, lastRoundOnly);
    },
    
    calculateTotalThrowsMatch(throws) {
      return ScorecardCalculations.calculateTotalThrowsMatch(throws);
    },
    
    calculateTotalBullseyesMatch(throws) {
      return ScorecardCalculations.calculateTotalBullseyesMatch(throws);
    },
    
    setupGeoLocation() {
      if (!("geolocation" in navigator)) {
        return;
      }

      navigator.geolocation.getCurrentPosition(pos => {
        this.$store.commit("setLocation", pos);
      }, err => { 
        console.error("Error Getting GeoLocation: " + err.message);
      })
    },
    
    submitBroadcastControl(item) {
      SocketioService.emitBroadcastControlItem(item);
    },
    
    submitPhotoControl(photo_item) {
      SocketioService.emitPhotoControlItem(photo_item);
    },
    
    shareScorecard(scorecard) {
      window.open('/summary/' + scorecard._id);
    },
    
    emailScorecard(scorecard) {
      const email_string = "mailto:?subject=Scorecard&body=Your game summary is here: https://axes2.ca/summary/" + scorecard._id + ".";
      window.open(email_string);
    },
    
    async downloadCSV(scorecard) {
      try {
        await scorecardService.downloadCSV(scorecard, this.getApiToken());
      } catch (error) {
        alert(error);
      }
    },
    
    async showScorecard(scorecard) {
      try {
        const data = await scorecardService.getScorecard(scorecard._id, this.getApiToken());
        this.showModal(data);
      } catch (error) {
        alert(error);
      }
    },
    
    async showNewSummaryModal(scorecard) {
      try {
        const data = await scorecardService.getScorecard(scorecard._id, this.getApiToken());
        this.showSummaryModal(data);
      } catch (error) {
        alert(error);
      }
    }
  },
  
  mounted() {
    this.getAdminUsers();
    setTimeout(() => {
      this.isLoading = false
    }, 1500);
    this.setupGeoLocation();
  }
}
</script>

<style lang="scss">
@font-face {
    font-family: '28 Days Later';
    src: url('fonts/28 Days Later.woff') format('woff');
}
html, body {
  background-color: black;
  color: white;
  height: 100%;
}
#app h1,h2,h3,h4,h5,h6 {
  color: white !important;
}
#app {
  background-color: #383838;
  font-family: '28 Days Later';
  letter-spacing: 3px;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  color: #2c3e50;
}
  @media print
{    
    .no-print, .no-print *
    {
        display: none !important;
    }
}
.slider-indicator-icon {
  display: none !important;
}
</style>